import React from 'react';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import type { Conversation } from '@/types/chat';

interface ConversationPreviewProps {
  conversation: Conversation;
  onSelect: (conversation: Conversation) => void;
  isHighlighted?: boolean;
  isCompact?: boolean;
}

const ConversationPreview: React.FC<ConversationPreviewProps> = ({
  conversation,
  onSelect,
  isHighlighted = false,
  isCompact = false
}) => {
  // Format last activity time
  const lastActivityTime = conversation.last_message_at
    ? formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })
    : '';

  const handleClick = () => {
    onSelect(conversation);
  };

  return (
    <div
      className={cn(
        "cursor-pointer transition-colors border-l-4 border-transparent hover:border-primary/30 hover:bg-muted/30",
        "px-2 sm:px-3 py-2 rounded-r-md relative",
        isHighlighted && "border-primary bg-primary/10",
        isCompact && "py-1.5"
      )}
      onClick={handleClick}
    >
      <div className="flex flex-col gap-2">
        {/* Top row: timestamp */}
        <div className="flex justify-end pt-1 pr-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {lastActivityTime}
          </span>
        </div>
        
        {/* Message content */}
        <div className="flex items-end justify-between gap-2 pl-2">
          <p className={cn(
            "text-sm sm:text-sm text-muted-foreground truncate flex-1",
            conversation.unreadCount > 0 && "font-medium text-foreground"
          )}>
            {conversation.lastMessage?.content || 'No messages yet'}
          </p>
          
          {/* Unread indicator - red pill */}
          {conversation.unreadCount > 0 && (
            <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mb-0.5" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationPreview;